/**
 * Shared "silent background refetch" concept — a page declares an interval and the query keys it
 * wants kept fresh, and gets a countdown pill + pause/resume + manual refresh for free. See
 * `useAutoRefresh` (hooks) and `AutoRefreshProvider` / `AutoRefreshIndicator` (components/shared).
 *
 * Pure TS only — never import lucide-react here, this file must stay safe for non-React consumers.
 */

export enum EAutoRefreshSuspendReason {
  TAB_HIDDEN = "tab-hidden",
  WINDOW_BLURRED = "window-blurred",
  MUTATION_IN_FLIGHT = "mutation-in-flight",
  DIALOG_OPEN = "dialog-open",
}

export const AUTO_REFRESH_DEFAULTS = {
  ENABLED: true,
  // Used only while no page has registered a config — the timer is disabled in that state, so
  // this value never actually drives a refresh, it just keeps the hook's math well-defined.
  FALLBACK_INTERVAL_SECONDS: 10,
  // How often the countdown/ring state recomputes. Short enough for a smooth ring animation,
  // long enough to stay cheap while a list/detail page is mounted.
  TICK_MS: 200,
} as const;

export const AUTO_REFRESH_RING_DIMENSIONS = {
  SIZE_PX: 16,
  STROKE_WIDTH_PX: 2,
} as const;

export const AUTO_REFRESH_LABELS = {
  REFRESHING: "Refreshing…",
  PAUSED: "Auto-refresh paused",
  ACTIVE: "Auto-refresh on",
  PAUSE_BUTTON_LABEL: "Pause auto-refresh",
  RESUME_BUTTON_LABEL: "Resume auto-refresh",
  REFRESH_NOW_BUTTON_LABEL: "Refresh now",
} as const;

export const getAutoRefreshCountdownLabel = (secondsRemaining: number): string =>
  `Refreshing in ${secondsRemaining}s`;
